/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { LoadingButton } from "@mui/lab"
import { isMedaicaApiError, MedaicaApiErrorType } from "@medaica/common/services/medaica-api-service"
import { logError } from "@medaica/common/services/util"
import { useHeartReportStores } from "../heart-report-context"
import { HeartReportCard, SupportLink } from "../components/heart-report-layout"
import CodeInput from "../../booking/components/code-input"
import heartReportCopy from "../heartReportCopy"

const copy = heartReportCopy.verify

const CODE_LENGTH = 6
const RESEND_COOLDOWN_SECONDS = 60
// After this many failed attempts, surface calmer guidance + a support link. Client-side
// only: a throttled session can't be flagged by the API, so this helps any stuck user.
const HELP_AFTER_ATTEMPTS = 3

const mapVerifyError = (error: unknown): string => {
  if (isMedaicaApiError(error)) {
    const apiError = error as { type: MedaicaApiErrorType; attemptsRemaining?: number }
    switch (apiError.type) {
      case MedaicaApiErrorType.CODE_LOCKED:
        return copy.errors.locked
      case MedaicaApiErrorType.CODE_EXPIRED:
        return copy.errors.expired
      case MedaicaApiErrorType.CODE_SEND_FAILED:
        return copy.errors.sendFailed
      case MedaicaApiErrorType.INVALID_DATA:
        // attemptsRemaining rides on both mock errors and (via the interceptor passthrough)
        // real API errors; show the count only when positive — never "0 left".
        return typeof apiError.attemptsRemaining === "number" && apiError.attemptsRemaining > 0
          ? copy.errors.wrongCode(apiError.attemptsRemaining)
          : copy.errors.wrongCodeGeneric
      default:
        return copy.errors.generic
    }
  }
  return copy.errors.generic
}

/** The six-box code gate; the same input and feedback pattern as the booking wizard's. */
const Verify = observer((): ReactElement => {
  const { sessionStore } = useHeartReportStores()
  const [code, setCode] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [resendCooldown, setResendCooldown] = useState(0)
  const [resent, setResent] = useState(false)

  useEffect(() => {
    if (resendCooldown <= 0) {
      return undefined
    }
    const timer = setTimeout(() => setResendCooldown((seconds) => seconds - 1), 1000)
    return () => clearTimeout(timer)
  }, [resendCooldown])

  const submit = async (value: string): Promise<void> => {
    if (value.length < CODE_LENGTH || submitting) {
      return
    }
    setSubmitting(true)
    setError(null)
    setResent(false)
    try {
      await sessionStore.submitCode(value)
      // navigation is handled by the store (→ download)
    } catch (submitError) {
      setError(mapVerifyError(submitError))
      setCode("")
      setAttempt((current) => current + 1)
    } finally {
      setSubmitting(false)
    }
  }

  const resend = async (): Promise<void> => {
    if (resendCooldown > 0) {
      return
    }
    setError(null)
    setResent(false)
    // Start the cooldown on click (not after the request resolves) so the button disables
    // immediately — otherwise rapid clicks fire several resendCode() calls. Reset on failure.
    setResendCooldown(RESEND_COOLDOWN_SECONDS)
    try {
      await sessionStore.resendCode()
      // A fresh code also resets the attempts server-side, so a locked gate opens again.
      setAttempt(0)
      setResent(true)
    } catch (resendError) {
      logError(resendError)
      setError(copy.errors.sendFailed)
      setResendCooldown(0)
    }
  }

  const resendButton = (className: string): ReactElement => (
    <>
      <button type="button" className={className} disabled={resendCooldown > 0} onClick={() => void resend()}>
        {copy.resend}
      </button>
      {resendCooldown > 0 ? ` (${resendCooldown}s)` : ""}
    </>
  )

  return (
    <HeartReportCard title={copy.title}>
      <p className="mt-2 mb-8 text-center text-gray-600">{copy.subtitle(sessionStore.maskedEmail ?? "your email")}</p>

      <CodeInput
        key={attempt}
        length={CODE_LENGTH}
        onChange={setCode}
        onComplete={(value) => void submit(value)}
        disabled={submitting}
      />

      {error ? (
        <div className="mt-3 text-center">
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
          {/* Same action as the footer link; resend() clears `error`, so this and the message
              above it disappear together. Offered for every error — a locked or expired code is
              cleared by a fresh one. */}
          <p className="mt-1 text-sm text-gray-500">
            {resendButton("font-medium text-red-600 underline disabled:text-gray-400 disabled:no-underline")}
          </p>
          {attempt >= HELP_AFTER_ATTEMPTS ? (
            <p className="mt-3 text-xs text-gray-500">
              {copy.stuckHelp} <SupportLink>{copy.contactSupport}</SupportLink>
            </p>
          ) : null}
        </div>
      ) : null}

      {resent ? <output className="mt-3 block text-center text-sm text-gray-600">{copy.resent}</output> : null}

      <LoadingButton
        fullWidth
        variant="contained"
        size="large"
        sx={{ mt: 3 }}
        loading={submitting}
        disabled={code.length < CODE_LENGTH}
        onClick={() => void submit(code)}
      >
        {copy.continueButton}
      </LoadingButton>

      <p className="mt-5 text-center text-sm text-gray-500">
        {copy.didntGetIt} {resendButton("font-medium text-primary disabled:text-gray-400")}
      </p>
    </HeartReportCard>
  )
})

export default Verify
